import { useState, useEffect, useCallback, useRef, ReactElement } from 'react';
import { useAuth } from '../authContext/useAuth';
import { useIdleTimer } from './useIdleTimer';

interface SessionManagerConfig {
  idleTimeout?: number;
  warningTimeout?: number;
}

interface SessionManagerReturn {
  showExpiringModal: boolean;
  remainingTime: number;
  resetTimer: () => void;
  getRemainingTime: () => number;
  handleStayLoggedIn: () => void;
  handleLogoutNow: () => Promise<void>;
}

export const useSessionManager = (config: SessionManagerConfig = {}): SessionManagerReturn => {
  const {
    idleTimeout = 30 * 60 * 1000, // 30 minutes
    warningTimeout = 29 * 60 * 1000, // 29 minutes
  } = config;

  const { logout, isAuthenticated } = useAuth();
  const [showExpiringModal, setShowExpiringModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleSessionWarning = useCallback(() => {
    setShowExpiringModal(true);
    setRemainingTime(60 * 1000);
  }, []);

  const handleSessionExpired = useCallback(async () => {
    setShowExpiringModal(false);
    await logout();
  }, [logout]);

  const { resetTimer, getRemainingTime } = useIdleTimer(
    isAuthenticated
      ? {
          timeout: idleTimeout,
          warningTimeout: warningTimeout,
          onWarning: handleSessionWarning,
          onIdle: handleSessionExpired,
        }
      : {
          timeout: 0,
          warningTimeout: 0,
          onWarning: () => {},
          onIdle: () => {},
        },
  );

  const handleStayLoggedIn = useCallback(() => {
    setShowExpiringModal(false);
    setRemainingTime(0);
    resetTimer();
  }, [resetTimer]);

  const handleLogoutNow = useCallback(async () => {
    setShowExpiringModal(false);
    await logout();
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowExpiringModal(false);
      setRemainingTime(0);
      return;
    }

    if (showExpiringModal && remainingTime > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          const newTime = prev - 1000;
          if (newTime <= 0) {
            setShowExpiringModal(false);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showExpiringModal, remainingTime, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      } else {
        resetTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [resetTimer, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleFocus = () => resetTimer();
    const handleBlur = () => {};

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [resetTimer, isAuthenticated]);

  return {
    showExpiringModal: isAuthenticated ? showExpiringModal : false,
    remainingTime,
    resetTimer,
    getRemainingTime,
    handleStayLoggedIn,
    handleLogoutNow,
  };
};
